/**
 * Autonomous loop rules, kept free of Angular so they can be tested on their own.
 *
 * A project's `loop_config` says whether the loop may run, how much freedom it has
 * and which parts of the project it may change. Each run produces findings, test
 * cases and changes. The server applies the same checks; this file lets the
 * screen say what is wrong before saving and which buttons a change can show.
 */

export type LoopAutonomy = 'propose' | 'auto_guarded' | 'full';
export type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
export type ChangeStatus = 'proposed' | 'applied' | 'rejected' | 'rolled_back' | 'blocked';
export type ChangeField = 'final_prompt' | 'initial_greeting' | 'whatsapp_chat_prompt';
export type ChangeRisk = 'low' | 'medium' | 'high';
export type ChangeAction = 'approve' | 'reject' | 'rollback';

export const AUTONOMY_LEVELS: LoopAutonomy[] = ['propose', 'auto_guarded', 'full'];
export const TOUCH_FIELDS: ChangeField[] = ['final_prompt', 'initial_greeting', 'whatsapp_chat_prompt'];

export const AUTONOMY_INFO: { [level: string]: { label: string; text: string } } = {
  propose: {
    label: 'Propose only',
    text: 'Propose only — it writes findings and changes; you approve each one'
  },
  auto_guarded: {
    label: 'Auto-apply, guarded',
    text: 'Auto-apply, guarded — it applies low-risk changes that pass replay tests; everything else waits for approval'
  },
  full: {
    label: 'Fully autonomous',
    text: 'Fully autonomous — it applies every change within what you allow below'
  }
};

export const FIELD_LABELS: { [field: string]: string } = {
  final_prompt: 'Call prompt',
  initial_greeting: 'Opening line',
  whatsapp_chat_prompt: 'WhatsApp prompt'
};

export interface NumberLimit {
  min: number;
  max: number;
  label: string;
}

export const LOOP_LIMITS: { [key: string]: NumberLimit } = {
  batch_size: { min: 10, max: 1000, label: 'Calls to sample per run' },
  max_test_cases: { min: 0, max: 50, label: 'Test cases per run' },
  max_changes_per_run: { min: 1, max: 10, label: 'Changes per run' },
  max_planned_tasks: { min: 0, max: 50, label: 'Planned calls per run' },
  token_budget: { min: 10000, max: 2000000, label: 'Token budget per run' },
  run_hour: { min: 0, max: 23, label: 'Run time' }
};

export interface LoopConfig {
  version: number;
  enabled: boolean;
  autonomy: LoopAutonomy;
  use_evals: boolean;
  write_test_cases: boolean;
  deep_read: boolean;
  batch_size: number;
  max_test_cases: number;
  max_changes_per_run: number;
  plan_tasks: boolean;
  max_planned_tasks: number;
  token_budget: number;
  allowed_touch: string[];
  /** Run by itself every day at run_hour (IST) until the end date. */
  autopilot: boolean;
  run_hour: number;
  /** YYYY-MM-DD, or null to stop at the campaign's end date. */
  autopilot_end_date: string | null;
}

export const DEFAULT_LOOP_CONFIG: LoopConfig = {
  version: 1,
  enabled: false,
  autonomy: 'propose',
  use_evals: true,
  write_test_cases: true,
  deep_read: true,
  batch_size: 1000,
  max_test_cases: 20,
  max_changes_per_run: 3,
  plan_tasks: false,
  max_planned_tasks: 10,
  token_budget: 400000,
  allowed_touch: [],
  autopilot: false,
  run_hour: 2,
  autopilot_end_date: null
};

export interface RunStats {
  calls_sampled: number;
  calls_with_evals: number;
  transcripts_read: number;
  test_cases: number;
  tokens_used: number;
  token_budget: number;
  /** How many days back the sampled calls reach (30, or 365 for a quiet project). */
  window_days?: number;
}

export interface AppliedChange {
  change_id: string;
  field: ChangeField;
  applied_at: number | null;
  rationale: string;
  expected_effect: string;
}

export interface RunSummary {
  run_id: string;
  status: RunStatus;
  trigger: string;
  created_at: number;
  started_at: number | null;
  finished_at: number | null;
  summary: string;
  error: string | null;
  stats: RunStats;
  findings_count: number;
  changes_count: number;
  changes_applied: number;
  applied_changes?: AppliedChange[];
}

export interface LoopFinding {
  id: string;
  title: string;
  detail: string;
  stage: string | null;
  evidence_calls: number;
  evidence_job_ids: string[];
}

export interface LoopTestCase {
  id: string;
  scenario: string;
  expected: string;
  source_job_id: string | null;
  result: { passed: boolean | null; note: string };
}

export interface LoopReplay {
  cases: number;
  passed_before: number;
  passed_after: number;
}

export interface LoopChange {
  change_id: string;
  field: ChangeField;
  before: string;
  after: string;
  rationale: string;
  expected_effect: string;
  risk: ChangeRisk;
  replay: LoopReplay | null;
  status: ChangeStatus;
  applied_at: number | null;
  applied_by: string | null;
  blocked_reason: string | null;
}

export interface PlannedTask {
  task_id: string;
  job_id: string;
  lead_label: string | null;
  execute_at_ist: string;
  reason: string;
  expected_effect: string;
  est_credits: number | null;
  status: 'proposed' | 'queued_for_approval' | 'failed';
  blocked_reason: string | null;
}

export interface LoopRun extends RunSummary {
  planned_tasks: PlannedTask[];
  findings: LoopFinding[];
  test_cases: LoopTestCase[];
  changes: LoopChange[];
  config_snapshot: LoopConfig | null;
}

export interface LoopRunsResponse {
  runs: RunSummary[];
}

export interface LoopRunResponse {
  run: LoopRun;
}

export interface StartRunResponse {
  run_id: string;
  status: RunStatus;
}

function isInt(n: any): boolean {
  return typeof n === 'number' && isFinite(n) && Math.floor(n) === n;
}

function pickInt(value: any, fallback: number): number {
  return isInt(value) ? value : fallback;
}

function pickBool(value: any, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

export function cloneLoopConfig(cfg: LoopConfig): LoopConfig {
  return JSON.parse(JSON.stringify(cfg));
}

/** The stored loop config, with defaults for anything missing. Never null. */
export function readLoopConfig(project: any): LoopConfig {
  const d = DEFAULT_LOOP_CONFIG;
  const raw = project && project.loop_config;
  if (!raw || typeof raw !== 'object') {
    return cloneLoopConfig(d);
  }
  const touch: string[] = [];
  const stored = Array.isArray(raw.allowed_touch) ? raw.allowed_touch : [];
  for (const f of TOUCH_FIELDS) {
    if (stored.indexOf(f) !== -1) { touch.push(f); }
  }
  return {
    version: 1,
    enabled: pickBool(raw.enabled, d.enabled),
    autonomy: AUTONOMY_LEVELS.indexOf(raw.autonomy) !== -1 ? raw.autonomy : d.autonomy,
    use_evals: pickBool(raw.use_evals, d.use_evals),
    write_test_cases: pickBool(raw.write_test_cases, d.write_test_cases),
    deep_read: pickBool(raw.deep_read, d.deep_read),
    batch_size: pickInt(raw.batch_size, d.batch_size),
    max_test_cases: pickInt(raw.max_test_cases, d.max_test_cases),
    max_changes_per_run: pickInt(raw.max_changes_per_run, d.max_changes_per_run),
    plan_tasks: pickBool(raw.plan_tasks, d.plan_tasks),
    max_planned_tasks: pickInt(raw.max_planned_tasks, d.max_planned_tasks),
    token_budget: pickInt(raw.token_budget, d.token_budget),
    allowed_touch: touch,
    autopilot: pickBool(raw.autopilot, d.autopilot),
    run_hour: pickInt(raw.run_hour, d.run_hour),
    autopilot_end_date: typeof raw.autopilot_end_date === 'string' && raw.autopilot_end_date ? raw.autopilot_end_date.slice(0, 10) : null
  };
}

/** True when the project has a stored loop config. */
export function hasStoredLoopConfig(project: any): boolean {
  return !!(project && project.loop_config && typeof project.loop_config === 'object');
}

/** Plain-language problems with a loop config. Empty means it can be saved. */
export function validateLoopConfig(cfg: LoopConfig): string[] {
  const errors: string[] = [];
  if (!cfg) { return ['Loop settings are missing.']; }
  if (AUTONOMY_LEVELS.indexOf(cfg.autonomy) === -1) {
    errors.push('Pick how much freedom the loop has.');
  }
  for (const key of Object.keys(LOOP_LIMITS)) {
    const limit = LOOP_LIMITS[key];
    const value = (cfg as any)[key];
    if (!isInt(value)) {
      errors.push(limit.label + ' must be a whole number.');
    } else if (value < limit.min || value > limit.max) {
      errors.push(limit.label + ' must be between ' + formatTokens(limit.min) + ' and ' + formatTokens(limit.max) + '.');
    }
  }
  if (cfg.autopilot_end_date && !/^\d{4}-\d{2}-\d{2}$/.test(cfg.autopilot_end_date)) {
    errors.push('The autopilot end date must be a date.');
  }
  const touch = cfg.allowed_touch || [];
  for (const f of touch) {
    if (TOUCH_FIELDS.indexOf(f as ChangeField) === -1) {
      errors.push('"' + f + '" is not something the loop can change.');
    }
  }
  return errors;
}

/** What is sent to the server: known keys only, touch list in a fixed order. */
export function toLoopPayload(cfg: LoopConfig): LoopConfig {
  const touch = TOUCH_FIELDS.filter(f => (cfg.allowed_touch || []).indexOf(f) !== -1);
  return {
    version: 1,
    enabled: !!cfg.enabled,
    autonomy: cfg.autonomy,
    use_evals: !!cfg.use_evals,
    write_test_cases: !!cfg.write_test_cases,
    deep_read: !!cfg.deep_read,
    batch_size: cfg.batch_size,
    max_test_cases: cfg.max_test_cases,
    max_changes_per_run: cfg.max_changes_per_run,
    plan_tasks: !!cfg.plan_tasks,
    max_planned_tasks: cfg.max_planned_tasks,
    token_budget: cfg.token_budget,
    allowed_touch: touch,
    autopilot: !!cfg.autopilot,
    run_hour: cfg.run_hour,
    autopilot_end_date: cfg.autopilot_end_date || null
  };
}

/** "2:00 AM" style label for an hour 0-23. */
export function hourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24;
  const suffix = h < 12 ? 'AM' : 'PM';
  const twelve = h % 12 === 0 ? 12 : h % 12;
  return twelve + ':00 ' + suffix;
}

/** The last day autopilot may run: the chosen date, else the campaign's end date, else null. */
export function autopilotEndDay(cfg: LoopConfig, project: any): string | null {
  if (cfg && cfg.autopilot_end_date) { return cfg.autopilot_end_date; }
  const campaign = project && project.campaign_config;
  const raw = campaign && campaign.campaign_end_date;
  return typeof raw === 'string' && raw.length >= 10 ? raw.slice(0, 10) : null;
}

/** One sentence saying when the loop runs by itself, for the schedule button. */
export function scheduleText(cfg: LoopConfig, project: any, todayIst: string): string {
  if (!cfg || !cfg.enabled) {
    return 'The loop is off, so nothing runs by itself.';
  }
  if (!cfg.autopilot) {
    return 'Autopilot is off. The loop only runs when you press Run now.';
  }
  const end = autopilotEndDay(cfg, project);
  if (!end) {
    return 'Autopilot is on, but there is no end date. Pick one, or set the campaign end date, or it will not run.';
  }
  if (end < todayIst) {
    return 'Autopilot ended on ' + end + '. Pick a later end date to run it again.';
  }
  return 'Runs every day at ' + hourLabel(cfg.run_hour) + ' India time until ' + end
    + (cfg.autopilot_end_date ? '' : ' (the campaign end date)') + '.';
}

/** Same settings, ignoring the order the touch list was ticked in. */
export function sameLoopConfig(a: LoopConfig, b: LoopConfig): boolean {
  if (!a || !b) { return a === b; }
  return JSON.stringify(toLoopPayload(a)) === JSON.stringify(toLoopPayload(b));
}

/** Tick or untick one thing the loop may change. */
export function toggleTouch(cfg: LoopConfig, field: string, on: boolean): LoopConfig {
  const next = cloneLoopConfig(cfg);
  const without = next.allowed_touch.filter(f => f !== field);
  next.allowed_touch = on ? without.concat([field]) : without;
  return next;
}

export function isRunActive(status: string): boolean {
  return status === 'queued' || status === 'running';
}

export function statusLabel(status: string): string {
  switch (status) {
    case 'queued': return 'Queued';
    case 'running': return 'Running';
    case 'completed': return 'Completed';
    case 'failed': return 'Failed';
    case 'proposed': return 'Waiting for approval';
    case 'applied': return 'Applied';
    case 'rejected': return 'Rejected';
    case 'rolled_back': return 'Rolled back';
    case 'blocked': return 'Blocked';
    default: return status || '–';
  }
}

export function riskLabel(risk: string): string {
  switch (risk) {
    case 'low': return 'Low risk';
    case 'medium': return 'Medium risk';
    case 'high': return 'High risk';
    default: return risk || '–';
  }
}

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] || field || '–';
}

export function triggerLabel(trigger: string): string {
  if (trigger === 'manual') { return 'Manual'; }
  if (trigger === 'scheduled') { return 'Scheduled'; }
  return trigger || '–';
}

/** Buttons a change can show, by status. */
export function changeActions(change: LoopChange): ChangeAction[] {
  if (!change) { return []; }
  if (change.status === 'proposed') { return ['approve', 'reject']; }
  if (change.status === 'applied') { return ['rollback']; }
  return [];
}

export function actionLabel(action: string): string {
  switch (action) {
    case 'approve': return 'Approve & apply';
    case 'reject': return 'Reject';
    case 'rollback': return 'Roll back';
    default: return action;
  }
}

/** Sentence shown in the confirm step before a decision is sent. */
export function confirmText(action: string, change: LoopChange): string {
  const what = change ? fieldLabel(change.field).toLowerCase() : 'this';
  if (action === 'approve') { return 'Apply this change to the ' + what + ' now? Live calls will use it.'; }
  if (action === 'rollback') { return 'Put the ' + what + ' back to how it was before this change?'; }
  return 'Reject this change? Nothing will be changed.';
}

/** 1,234 -> "1,234"; 400000 -> "400,000"; null/NaN -> "–". */
export function formatTokens(n: number | null | undefined): string {
  if (n === null || n === undefined || typeof n !== 'number' || !isFinite(n)) { return '–'; }
  const rounded = Math.round(n);
  const sign = rounded < 0 ? '-' : '';
  const digits = String(Math.abs(rounded));
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/** "52,000 of 400,000 tokens" */
export function tokensText(stats: RunStats | null | undefined): string {
  if (!stats) { return '–'; }
  return formatTokens(stats.tokens_used) + ' of ' + formatTokens(stats.token_budget);
}

/** Share of the token budget used, 0-100. */
export function tokenShare(stats: RunStats | null | undefined): number {
  if (!stats || !stats.token_budget || stats.token_budget <= 0) { return 0; }
  const share = ((stats.tokens_used || 0) / stats.token_budget) * 100;
  return Math.max(0, Math.min(100, Math.round(share)));
}

/** "passed 6 of 10 before, 9 of 10 after" */
export function replayText(replay: LoopReplay | null | undefined): string {
  if (!replay) { return 'Not replayed'; }
  return 'Passed ' + replay.passed_before + ' of ' + replay.cases + ' before, ' +
    replay.passed_after + ' of ' + replay.cases + ' after';
}

export function testResultLabel(tc: LoopTestCase): string {
  if (!tc || !tc.result || tc.result.passed === null || tc.result.passed === undefined) { return 'Not run'; }
  return tc.result.passed ? 'Passed' : 'Failed';
}

/** One line for the runs list. */
export function summariseRun(run: RunSummary): string {
  if (!run) { return ''; }
  if (run.status === 'queued') { return 'Waiting to start'; }
  if (run.status === 'running') { return 'In progress'; }
  if (run.status === 'failed') { return run.error || 'The run failed'; }
  const parts: string[] = [];
  const calls = run.stats ? run.stats.calls_sampled : 0;
  parts.push(calls + ' call' + (calls === 1 ? '' : 's') + ' sampled');
  parts.push(run.findings_count + ' finding' + (run.findings_count === 1 ? '' : 's'));
  parts.push(run.changes_count + ' change' + (run.changes_count === 1 ? '' : 's') + ' proposed, ' + run.changes_applied + ' applied');
  return parts.join(', ');
}

/** Newest first, by created time. Does not change the input. */
export function sortRuns<T extends RunSummary>(runs: T[]): T[] {
  return (runs || []).slice().sort((a, b) => (b.created_at || 0) - (a.created_at || 0));
}

/** True when any run in the list is still queued or running. */
export function anyActive(runs: RunSummary[]): boolean {
  return (runs || []).some(r => isRunActive(r.status));
}

/** Epoch seconds -> "4 Nov 2026, 3:05 pm" in IST; "–" when missing. */
export function formatEpoch(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds as number)) { return '–'; }
  const d = new Date((seconds as number) * 1000 + 330 * 60000);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  let h = d.getUTCHours();
  const m = d.getUTCMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 === 0 ? 12 : h % 12;
  return d.getUTCDate() + ' ' + months[d.getUTCMonth()] + ' ' + d.getUTCFullYear() + ', ' +
    h + ':' + (m < 10 ? '0' : '') + m + ' ' + ampm;
}
